import { Avatar,Col, Flex, Input, Row, Select, Table, Tag,Card,Button,Typography } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate, getStatusNameInArabicAsWell } from "../../../../../../utils/utils";
import { colors } from "../../../../../../constants/colors";
import styles from "./User.module.scss";
import {
  getCustomersAction,
  updateCustomerStatusAction,
} from "../../../../../../redux/services/customers/customersServices";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import useDebounce from "../../../../../../hooks/useDebounce";
import { SearchOutlined,UserOutlined } from "@ant-design/icons";
import { toastService } from "../../../../../common/toast-messages/toast";

const User = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { customers } = useSelector((state) => state.customers);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: customers?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  /* filter dependencies */

  /* function definition to get table data */
  const handleGetTableData = (tempPageNo, tempPageSize, isUpdateStatus = false) => {
    setLoading(true);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : isUpdateStatus ? tempPageNo : pagination?.pageNo,
      limit: isUpdateStatus ? tempPageSize : pagination?.pageSize,
      role: ["customer", "staff", "medical_staff", "doctor"],
      search: debouncedSearchValue,
    };
    dispatch(getCustomersAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* on status changed */
  const onStatusChanged = async (id, value, record) => {
    const dataToBeSent = {};
    dataToBeSent.selectedCustomerId = record?._id;
    dataToBeSent["status"] = value?.value;

    try {
      await dispatch(updateCustomerStatusAction(dataToBeSent, setLoading))
      handleGetTableData(pagination.pageNo,pagination.pageSize, true)

    } catch (error) {
      toastService.error(error)
    }
  };
  /* on status changed */

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("Modules.Provider.ID"),
        dataIndex: "id_number",
        key: "id_number",
        render: (_, record) => record?.id_number ?? "S-" + record?.staff_number,
      },
      {
        title: t("Modules.Configuration.userType"),
        dataIndex: "role",
        key: "role",
      },
      {
        title: t("chats.userName"),
        render: (_,record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                shape="large"
                src={record?.profile_image}
                key={record?.profile_image}
                icon={<UserOutlined />}
              />
               {
                record?.first_name
            ? `${record?.first_name} ${record?.last_name}`
            : "-"
               }
            </Flex>
          );
        },
      },
      {
        title: t("chats.userEmail"),
        render: (_, record) => (record?.email ? `${record?.email}` : "-"),
      },
      {
        title: t("Modules.Provider.dateofJoining"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("Modules.AutoCredit.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "active":
                return { background: colors.lightTeal, color: colors.darkTeal };

              case "inactive":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "suspended":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "deleted":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getColorBasedOnStatus(record?.status),
                  textTransform: "capitalize",
                }}
              >
                {getStatusNameInArabicAsWell(String(record?.status))}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Select
                options={[
                  { value: "active", label: t("CommonUtils.active")},
                  { value: "inactive", label: t("CommonUtils.inactive")},
                ]}
                placeholder="Select Status"
                defaultValue={record?.status}
                style={{ width: 120 }}
                onChange={(id, value) => onStatusChanged(id, value, record)}
              />
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, [customers?.data]);
  /* initial dependencies rendering */

  return (
    <div className={styles.userContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={[24]} justify="space-between">
          <Col span={8}>
          <Card
              bordered
              hoverable
              style={{ 
                width: 240, padding: 0, borderRadius: 20,
                border: `1px solid ${colors.oceanBlue}`,
              }}
            >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={8}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<UserOutlined style={{ fontSize: 22 }} />}
                  />
                </Col>
                <Col span={16}>
                  <Flex vertical>
                    <Typography.Title level={4}>
                      {t("AppModuleNames.SuperAdmin.users")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.electricBlue }}
                    >
                      {customers?.total || 0}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByNameEmail")}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={customers?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default User;
